import React from 'react';
import PropTypes from 'prop-types';
import CalciteButton from '@/components/shared/CalciteButton';
import {useDispatch} from 'react-redux';
import {useGetCartQuery, useCreateCartMutation, useUpdateCartMutation} from '@/services/api/cart-api';
import {useGetLocaleQuery} from '@/services/api/locale-api';
import {showNotification} from '@/features/cart/slices/cart-notification-slice';
import {cartDataSource, toCartModel, mergeLineItems} from '@/utils/ecommerce/cart';
import {cloneDeep} from 'lodash-es';
import {useAnalytics} from '@/services/analytics/hooks/useAnalytics';
import {EVENTS, ANALYTICS_SOURCE_TYPES} from '@/services/analytics/constants';
import {setCartId} from '@/features/cart/slices/cart-slice';
import {getTrackingMetadata} from '@/services/api/utils/tracking-utils';
import {useErrorHandler} from '@/hooks/useErrorHandler';

const AddToCartButton = ({
  zone,
  cartId,
  items,
  children,
  onSuccess,
  onError,
  allowUpdate = true,
  notifyOnAdd = true,
  pricingLocale,
  additionalCartMetadata = {},
  onBeforeSubmit,
  ...restProps
}) => {
  const dispatch = useDispatch();
  const {data: locale} = useGetLocaleQuery({zone: zone}, {skip: !zone || !!pricingLocale});
  const {data: cart, isLoading} = useGetCartQuery(cartId, {skip: !cartId || !allowUpdate});
  const [createCart, {isLoading: isCreating}] = useCreateCartMutation({fixedCacheKey: 'create-cart'});
  const [updateCart, {isLoading: isUpdating}] = useUpdateCartMutation({fixedCacheKey: 'update-cart'});
  const {trackEvent} = useAnalytics();
  const {handleError} = useErrorHandler();

  const createNewCart = async (newLineItems) => {
    const trackingMetadata = getTrackingMetadata();
    const newCart = pricingLocale
      ? toCartModel(
          {...pricingLocale, cartMetadata: {...trackingMetadata, ...additionalCartMetadata}},
          cartDataSource.PRICING_LOCALE,
        )
      : toCartModel(
          {locale, cartMetadata: {...trackingMetadata, ...additionalCartMetadata}},
          cartDataSource.LOCALE,
        );
    newCart.lineItems = newLineItems;
    const cartResponse = await createCart(newCart).unwrap();
    dispatch(setCartId(cartResponse._id));
    return cartResponse;
  };

  const updateExistingCart = async (newLineItems) => {
    const updatedCart = cloneDeep(cart);
    updatedCart.lineItems = mergeLineItems(updatedCart.lineItems, newLineItems);
    const cartResponse = await updateCart(updatedCart).unwrap();
    return cartResponse;
  };

  const trackCartEvent = (cartResponse, newLineItems, isNewCart) => {
    const eventType = isNewCart ? EVENTS.CART_ADD_OPEN : EVENTS.CART_ADD;
    const cart = isNewCart ? cartResponse : {...cartResponse, lineItems: newLineItems};
    trackEvent(eventType, cart, {sourceType: ANALYTICS_SOURCE_TYPES.CART});
  };

  const updateCartUI = (newLineItems, cartResponse) => {
    const addedCount = newLineItems.reduce((total, item) => total + item.quantity, 0);
    const totalCount = cartResponse?.lineItems?.reduce((total, item) => total + item.quantity, 0);

    // Update global nav cart icon
    globalThis.esriShoppingCart.addItemsToCart(addedCount);
    if (notifyOnAdd) {
      dispatch(
        showNotification({
          items: newLineItems,
          totalQuantity: totalCount,
        }),
      );
    }
  };

  const handleAddToCart = async () => {
    if (typeof onBeforeSubmit === 'function') {
      const proceed = await Promise.resolve(onBeforeSubmit());
      if (proceed === false) return;
    }
    if (isLoading || isCreating || (allowUpdate && isUpdating) || !Array.isArray(items) || !items.length) return; // Prevent action while loading

    try {
      const newLineItems = items;
      const isNewCart = !allowUpdate || !cart;

      const cartResponse = isNewCart ? await createNewCart(newLineItems) : await updateExistingCart(newLineItems);

      trackCartEvent(cartResponse, newLineItems, isNewCart);
      updateCartUI(newLineItems, cartResponse);

      if (typeof onSuccess === 'function') onSuccess(cartResponse);
    } catch (error) {
      handleError(error);
      if (typeof onError === 'function') onError(error);
    }
  };

  return (
    <CalciteButton
      disabled={isCreating || (allowUpdate && isUpdating) || isLoading || undefined}
      onClick={handleAddToCart}
      {...restProps}
    >
      {children}
    </CalciteButton>
  );
};

AddToCartButton.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      materialNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      quantity: PropTypes.number,
      price: PropTypes.number,
      productName: PropTypes.string,
    }),
  ).isRequired,
  cartId: PropTypes.string,
  zone: PropTypes.string,
  allowUpdate: PropTypes.bool,
  notifyOnAdd: PropTypes.bool,
  pricingLocale: PropTypes.shape({
    currency: PropTypes.string,
    locale: PropTypes.string,
    isOmni: PropTypes.bool,
    storeDomain: PropTypes.string,
    formattedLocale: PropTypes.string,
    zone: PropTypes.string,
    currencySymbol: PropTypes.string,
    currencyPlacement: PropTypes.string,
    distributorNumber: PropTypes.string,
    paymentOptionAssets: PropTypes.array,
  }),
  additionalCartMetadata: PropTypes.object,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onBeforeSubmit: PropTypes.func,
  children: PropTypes.node,
};

export default AddToCartButton;
